<template>
  <div class="tw-relative tw-h-80 tw-w-full">
    <t-chart-skeleton v-if="loading" extended />
    <div
      v-if="isChartEmpty && !loading"
      class="tw-grid tw-grid-flow-row tw-auto-rows-max tw-place-items-center tw-gap-4 tw-pt-10"
    >
      <span
        class="tw-pointer-events-none tw-flex tw-items-center tw-pl-3 tw-text-primary-grey-200"
      >
        <svg
          width="200"
          height="auto"
          viewBox="0 0 216 162"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M28.0434 49.0052C43.0137 41.1203 83.9358 40.6259 92.4192 61.8175C100.903 83.0092 169.04 69.7317 179.321 84.2924C189.602 98.8531 168.619 101.379 194.17 114.376C219.721 127.373 202.665 152 199.423 152C196.181 152 22.1584 152 22.1584 152C22.1584 152 31.2963 128.808 16.8461 107.878C2.39581 86.9491 13.0731 56.8901 28.0434 49.0052Z"
            fill="#E7E8E9"
          />
          <path
            d="M106.747 43.3005L70.5336 28.8246C68.4823 28.0046 66.1546 29.0028 65.3346 31.0541C64.5146 33.1054 65.5128 35.4331 67.5642 36.2531L103.778 50.729C105.829 51.549 108.157 50.5508 108.977 48.4995C109.797 46.4481 108.799 44.1205 106.747 43.3005Z"
            fill="#BBE2DA"
          />
          <path
            d="M169.825 26.7869L150.282 9.63825C148.622 8.18118 146.095 8.3461 144.638 10.0066C143.181 11.6671 143.345 14.1944 145.006 15.6514L164.549 32.8001C166.209 34.2571 168.737 34.0922 170.194 32.4317C171.651 30.7712 171.486 28.2439 169.825 26.7869Z"
            fill="#BBE2DA"
          />
          <path
            d="M111.104 50.7479L148.576 17.6444C150.232 16.1818 150.389 13.6539 148.926 11.9983C147.463 10.3427 144.936 10.1862 143.28 11.6488L105.808 44.7524C104.152 46.215 103.996 48.7428 105.458 50.3984C106.921 52.0541 109.449 52.2105 111.104 50.7479Z"
            fill="#BBE2DA"
          />
          <path
            d="M30.0793 73.6628L70.4932 34.8978C72.0875 33.3686 72.1402 30.8365 70.611 29.2422C69.0817 27.6479 66.5496 27.5952 64.9553 29.1244L24.5415 67.8894C22.9472 69.4186 22.8945 71.9507 24.4237 73.545C25.953 75.1393 28.4851 75.192 30.0793 73.6628Z"
            fill="#BBE2DA"
          />
          <path d="M80 66H56V152H80V66Z" fill="#76C6B5" />
          <path d="M59 61H77L80 66H56L59 61Z" fill="#97D3C6" />
          <path d="M120 88H96V152H120V88Z" fill="#76C6B5" />
          <path d="M99 83H117L120 88H96L99 83Z" fill="#97D3C6" />
          <path d="M160 42H136V152H160V42Z" fill="#76C6B5" />
          <path d="M139 37H157L160 42H136L139 37Z" fill="#97D3C6" />
          <path
            d="M68 39C71.3137 39 74 36.3137 74 33C74 29.6863 71.3137 27 68 27C64.6863 27 62 29.6863 62 33C62 36.3137 64.6863 39 68 39Z"
            fill="#76C6B5"
          />
          <path
            d="M108 53C111.314 53 114 50.3137 114 47C114 43.6863 111.314 41 108 41C104.686 41 102 43.6863 102 47C102 50.3137 104.686 53 108 53Z"
            fill="#76C6B5"
          />
          <path
            d="M148 20C151.314 20 154 17.3137 154 14C154 10.6863 151.314 8 148 8C144.686 8 142 10.6863 142 14C142 17.3137 144.686 20 148 20Z"
            fill="#76C6B5"
          />
        </svg>
      </span>
      <div class="tw-flex tw-flex-col tw-items-center">
        <span class="tw-font-semibold">{{ t("no-data") }}</span>
        <span class="tw-text-sm tw-text-primary-grey-400">
          {{ t("subtext-license-usage") }}
        </span>
      </div>
    </div>
    <Bar
      v-if="!loading && !isChartEmpty"
      id="usage-chart"
      :data="chartData"
      :options="chartOptions"
      :style="chartStyle"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, toRefs, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";

import "chart.js/auto";
import { Bar } from "vue-chartjs";
import { ChartOptions } from "chart.js";
import TChartSkeleton from "@/components/TChartSkeleton/TChartSkeleton.vue";

interface LicenceHistoryGraphData {
  date: string;
  max_seats_used: number;
  seats_issued: number;
  seats_purchased: number;
}
interface ChartData {
  labels: string[];
  datasets: any[]; // eslint-disable-line
}

interface UsagePerCustomerProps {
  selectedServiceLicenceUuid: string;
}

const props = defineProps<UsagePerCustomerProps>();
const { selectedServiceLicenceUuid } = toRefs(props);

const { t } = useI18n();
const licenceStore = useLicenceStore();
const { getLicenceGraph } = storeToRefs(licenceStore);

const color = ref({
  default: "rgba(33, 186, 160, 1)",
  half: "rgba(33, 186, 160, 0.5)",
  quarter: "rgba(33, 186, 160, 0.25)",
  zero: "rgba(33, 186, 160, 0)",
});
const style = ref(getComputedStyle(document.body));
const styleColorGray = ref(style.value.getPropertyValue("--chartGrey").trim());
const styleColorWhite = ref(
  style.value.getPropertyValue("--chartWhite").trim(),
);
const gradientLineStyle = ref<CanvasGradient | string>(color.value.quarter);

const loading = ref<boolean>(true);
const isChartEmpty = ref<boolean>(false);
const chartData = ref<ChartData>({
  labels: [],
  datasets: [],
});
const chartOptions = ref<ChartOptions<"bar">>({
  clip: false,
  responsive: true,
  interaction: {
    intersect: false,
    mode: "index",
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      grid: {
        display: false,
        drawTicks: false,
      },
      border: {
        display: false,
        dash: [2, 4],
      },
      ticks: {
        padding: 10,
        color: styleColorWhite.value,
      },
      offset: true,
    },
    y: {
      offset: false,
      border: {
        display: false,
        dash: [2, 4],
      },
      grid: {
        drawTicks: false,
        color: styleColorGray.value,
        lineWidth: 2,
      },
      ticks: {
        padding: 1,
        stepSize: 250,
        precision: 0,
        color: styleColorWhite.value,
      },
    },
  },
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        usePointStyle: true,
        color: styleColorWhite.value,
        padding: 24,
      },
    },
  },
});
const chartStyle = ref({
  height: "auto",
  width: "100",
});
const formatGraphData = (
  data: LicenceHistoryGraphData[] | undefined | null,
) => {
  if (!Array.isArray(data) || data.length === 0) {
    isChartEmpty.value = true;
    return {
      labels: [],
      datasets: [],
    };
  }

  const labels = data.map((item) => item.date);
  const seats_purchased: number[] = [];
  const max_seats_used: number[] = [];
  const seats_issued: number[] = [];

  labels.forEach((label) => {
    const matchedItem = data.find((item) => item.date === label) || {
      seats_purchased: 0,
      max_seats_used: 0,
      seats_issued: 0,
    };

    seats_purchased.push(matchedItem.seats_purchased || 0);
    max_seats_used.push(matchedItem.max_seats_used || 0);
    seats_issued.push(matchedItem.seats_issued || 0);
  });

  const dateObjects = labels.map((dateString) => new Date(dateString));
  dateObjects.sort((a, b) => a.getTime() - b.getTime());

  const formattedLabels = dateObjects.map((date) => {
    const month = new Intl.DateTimeFormat("en", { month: "short" }).format(
      date,
    );
    const year = date.getFullYear();
    return `${month} ${year}`;
  });

  isChartEmpty.value = [seats_purchased, max_seats_used, seats_issued].every(
    (array) => array.every((value) => value === 0),
  );

  return {
    labels: formattedLabels,
    datasets: [
      {
        label: t("active-users"),
        data: max_seats_used,
        backgroundColor: "#fc9a00",
        order: 0,
        type: "bar",
        barPercentage: 0.5,
      },
      {
        label: t("licenses-purchased-legend"),
        data: seats_purchased,
        backgroundColor: "#4fa1ee",
        order: 2,
        type: "bar",
        barPercentage: 0.5,
      },
    ],
  };
};

const getLicenceHistoryByService = async (serviceUuid: string) => {
  loading.value = true;
  await licenceStore.getLicenceHistoryByService({
    service_uuid: serviceUuid,
  });
  loading.value = false;
};

const createGradientStyle = async (ctx: CanvasRenderingContext2D) => {
  const createdGradient = ctx.createLinearGradient(0, 0, 0, 250);
  createdGradient.addColorStop(0, color.value.half);
  createdGradient.addColorStop(0.35, color.value.quarter);
  createdGradient.addColorStop(1, color.value.zero);
  gradientLineStyle.value = createdGradient;
};

watch(selectedServiceLicenceUuid, async (selectedServiceLicenceUuid) => {
  await getLicenceHistoryByService(selectedServiceLicenceUuid);
});

watch(getLicenceGraph, (newLicenceGraphData) => {
  chartData.value = formatGraphData(newLicenceGraphData);
});

onMounted(async () => {
  if (!isChartEmpty.value && selectedServiceLicenceUuid.value) {
    const canvas = document.getElementById("usage-chart") as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      await createGradientStyle(ctx);
    }
  }
});
</script>
